import type { LearnTopic } from "../topic-types";

/**
 * Fight, Flight, Freeze & Fawn — trauma responses.
 *
 * Add new guides, exercises, prompts or worksheets by appending to a
 * section's `resources` array, or add a new section entirely.
 */
export const TRAUMA_RESPONSES_TOPIC: LearnTopic = {
  slug: "trauma-responses",
  emoji: "🌗",
  title: "Fight, Flight, Freeze & Fawn",
  tagline: "Recognising your trauma responses",
  theme: "sage",
  seoDescription:
    "Understand the fight, flight, freeze and fawn trauma responses — what they are, how they show up in everyday life, and gentle ways to notice and work with your own patterns. Trauma-informed education from Recovery Trauma™.",
  category: "cptsd-responses",
  cardBlurb: "Learn the four survival responses and how to recognise your own patterns.",
  isNew: true,
  intro: [
    "When something feels threatening, your nervous system doesn't wait for permission — it acts. Fight, flight, freeze and fawn are the body's built-in ways of trying to keep you safe, and they can switch on in a fraction of a second, long before conscious thought catches up.",
    "These responses aren't character flaws or overreactions. They are patterns that once made sense, often shaped by experiences where safety wasn't guaranteed. Many people carry one or more of these responses into adulthood, where they can show up in work, relationships, conflict, and even something as small as a text message left unanswered.",
    "This guide is educational, not diagnostic. Recognising a trauma response in yourself doesn't mean something is wrong with you — it means your body learned to protect you, and it may be worth learning what it's protecting you from now.",
  ],
  closing:
    "Trauma responses are the nervous system's attempt at protection, not evidence of brokenness. Learning to recognise your own patterns is a skill that develops with time and self-compassion. If these responses feel overwhelming or are affecting your daily life significantly, a trauma-informed therapist can offer support tailored to you.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What the four trauma responses are and where they come from.",
      intro:
        "Some plain-English grounding in what these responses are before looking at how they show up for you specifically.",
      resources: [
        {
          slug: "what-are-trauma-responses",
          kind: "learn",
          title: "What are trauma responses?",
          summary: "The nervous system's built-in strategies for handling threat.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "When your brain senses danger — real or perceived — a part of it called the amygdala can trigger a survival response before you've had a chance to think it through. This is sometimes called the body's alarm system, and it evolved to keep us alive, not to make sense of modern life such as awkward emails or difficult conversations.",
            },
            {
              type: "prose",
              text: "Fight, flight, freeze and fawn are four broad strategies the nervous system can reach for. Most people don't experience just one — you may notice a blend, or find that different situations call up different responses.",
            },
            {
              type: "list",
              title: "A few things worth knowing",
              items: [
                "These responses are automatic — they are not a choice you make in the moment",
                "They can be triggered by things that resemble past threat, even if the current situation is safe",
                "Having a strong trauma response doesn't mean something is 'wrong' with you",
                "Responses can change over time, and often soften with support and safety",
              ],
            },
            {
              type: "note",
              text: "This page describes patterns, not a diagnosis. Only a qualified professional can assess whether something meets the criteria for a clinical condition such as PTSD or C-PTSD.",
            },
          ],
        },
        {
          slug: "the-fight-response",
          kind: "learn",
          title: "The fight response",
          summary: "Confronting, controlling, or pushing back against a threat.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Fight is the response that moves towards the perceived threat rather than away from it — confronting, arguing, controlling or asserting power. It can look like anger, but underneath it is often an attempt to feel safe by taking charge before something bad happens.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Getting defensive or argumentative quickly, sometimes before fully understanding the situation",
                "A strong need to be right, or to have the last word",
                "Irritability that feels disproportionate to what triggered it",
                "A pull towards controlling situations, plans or other people",
                "Physical tension — clenched jaw, tight fists, a racing heart",
              ],
            },
            {
              type: "quote",
              text: "\"If I push back hard enough, nothing can get to me.\"",
            },
          ],
        },
        {
          slug: "the-flight-response",
          kind: "learn",
          title: "The flight response",
          summary: "Escaping, avoiding, or staying constantly busy.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Flight moves away from threat — physically leaving, or finding ways to avoid a situation altogether. In everyday life this doesn't always mean literally running; it can show up as busyness, overworking, or a restless need to keep moving so difficult feelings never catch up.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Leaving conversations, rooms or relationships when things feel tense",
                "Overworking, over-scheduling, or staying constantly 'productive'",
                "Restlessness, difficulty sitting still, or racing thoughts",
                "Avoiding people, places or topics associated with past difficulty",
                "A tight chest, shallow breathing, or an urge to bolt",
              ],
            },
            {
              type: "quote",
              text: "\"If I can just get away from this, I'll be okay.\"",
            },
          ],
        },
        {
          slug: "the-freeze-response",
          kind: "learn",
          title: "The freeze response",
          summary: "Going still, blank, or numb when a threat feels inescapable.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Freeze happens when fight or flight don't feel possible — when a threat seems inescapable, the nervous system can shut things down instead. This can range from a brief pause to a much deeper sense of shutdown, sometimes described as feeling frozen, foggy, or far away from your own body.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Going blank or 'spacing out' during stress or conflict",
                "Feeling unable to speak, move or make a decision in the moment",
                "A sense of heaviness, numbness or disconnection from the body",
                "Difficulty remembering what happened during a stressful event",
                "Feeling stuck, even when you logically know what you 'should' do",
              ],
            },
            {
              type: "note",
              text: "Freeze is closely linked to dissociation. If this pattern feels frequent or distressing, our Dissociation guide covers it in more depth.",
            },
          ],
        },
        {
          slug: "the-fawn-response",
          kind: "learn",
          title: "The fawn response",
          summary: "Appeasing, accommodating and people-pleasing to stay safe.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Fawn is a response that tries to prevent threat by keeping others calm, happy or comfortable — often at the cost of your own needs. It's sometimes the least talked-about of the four, but for many people, especially those who grew up needing to manage a parent's or caregiver's mood, it can be the most familiar.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Agreeing quickly, even when you don't actually agree",
                "Apologising often, sometimes for things that aren't your fault",
                "Difficulty saying no, or feeling guilty when you do",
                "Scanning others' moods closely and adjusting yourself to match",
                "Feeling responsible for other people's feelings or reactions",
              ],
            },
            {
              type: "quote",
              text: "\"If I keep everyone else okay, I'll be safe.\"",
            },
            {
              type: "note",
              text: "Our People-Pleasing guide explores the fawn response in much more depth, including where it can come from and how to gently build boundaries.",
            },
          ],
        },
        {
          slug: "understanding-shutdown",
          kind: "learn",
          title: "Understanding shutdown",
          summary: "The deeper, more prolonged form of freeze.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Shutdown is sometimes described as a more intense or prolonged version of freeze — a state where the body conserves energy by withdrawing, going still, or 'switching off' emotionally. It can follow a period of intense stress, or arrive when someone has felt trapped or powerless for a long time.",
            },
            {
              type: "list",
              title: "You might notice",
              items: [
                "A heavy tiredness that doesn't match how much you've slept",
                "Feeling emotionally flat, distant or 'checked out'",
                "Withdrawing from people or activities you'd usually enjoy",
                "A sense of being on autopilot",
                "Difficulty accessing feelings at all, even ones you know are there",
              ],
            },
            {
              type: "note",
              text: "Shutdown can sometimes overlap with symptoms of depression or dissociation. If it persists or worries you, it's worth speaking with a GP or mental health professional.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "How these responses show up in everyday, ordinary moments.",
      intro:
        "Trauma responses rarely announce themselves clearly. They tend to hide inside ordinary moments — a delayed reply, a raised voice, a full inbox.",
      resources: [
        {
          slug: "responses-in-everyday-life",
          kind: "learn",
          title: "How responses show up in everyday life",
          summary: "Work, family, conflict, texting and dating — real-life examples.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Trauma responses don't only appear during dramatic events. They often show up in small, everyday situations, sometimes so quietly that they can be mistaken for personality traits rather than protective patterns.",
            },
            {
              type: "list",
              title: "At work",
              items: [
                "Fight: pushing back hard on feedback, or needing to win every disagreement",
                "Flight: overworking, taking on too much, avoiding performance reviews",
                "Freeze: going blank when put on the spot in meetings",
                "Fawn: saying yes to every request, staying quiet about unfair workloads",
              ],
            },
            {
              type: "list",
              title: "In family relationships",
              items: [
                "Fight: arguments that escalate quickly over small things",
                "Flight: leaving gatherings early, staying physically distant",
                "Freeze: going silent during family conflict",
                "Fawn: keeping the peace at your own expense, over-explaining yourself",
              ],
            },
            {
              type: "list",
              title: "In conflict",
              items: [
                "Fight: raising your voice, feeling an urge to 'win' the argument",
                "Flight: wanting to leave the room or the relationship altogether",
                "Freeze: forgetting what you wanted to say, feeling stuck",
                "Fawn: apologising first, agreeing just to end the tension",
              ],
            },
            {
              type: "list",
              title: "In texting and dating",
              items: [
                "Fight: firing off a sharp reply before thinking it through",
                "Flight: leaving someone 'on read', pulling away when things get close",
                "Freeze: staring at a message for hours, unable to reply",
                "Fawn: mirroring the other person's interests, agreeing to plans you don't want",
              ],
            },
            {
              type: "note",
              text: "Recognising yourself in several of these doesn't mean you're 'doing trauma wrong'. Most people carry a mixture, and the mix can shift depending on who they're with and how safe they feel.",
            },
          ],
        },
        {
          slug: "body-signals",
          kind: "learn",
          title: "Body signals to notice",
          summary: "How trauma responses often announce themselves physically first.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Trauma responses are physical before they're psychological. Learning to notice the body signals early can create a small window to respond differently, rather than being swept along automatically.",
            },
            {
              type: "list",
              title: "Common signals",
              items: [
                "Fight: jaw or fist clenching, heat in the chest or face, a fast heartbeat",
                "Flight: restlessness, an urge to move, shallow or quick breathing",
                "Freeze: heaviness, cold hands, a sense of the body 'switching off'",
                "Fawn: a knot in the stomach, a tight smile, rapid nodding or agreeing",
              ],
            },
          ],
        },
        {
          slug: "which-responses-do-i-recognise",
          kind: "reflect",
          title: "Which responses do I recognise in myself?",
          summary: "A gentle, honest look at your own patterns — no right answers.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "This is a reflection exercise, not a test. There's no 'correct' number of responses to recognise, and it's completely normal to see yourself in more than one — sometimes in all four, depending on the situation. You can stop at any point, and come back to this whenever it feels useful.",
            },
            {
              type: "prompts",
              title: "Start broad",
              items: [
                "When I think about the last time I felt threatened, criticised or overwhelmed, what did my body do first?",
                "Which of the four responses (fight, flight, freeze, fawn) feels most familiar overall?",
                "Is there a second response that shows up when the first one doesn't work?",
              ],
            },
            {
              type: "prompts",
              title: "Get specific",
              items: [
                "How does this pattern show up with people I love versus people I don't know well?",
                "How does it show up at work compared to at home?",
                "Has this pattern changed over time, or does it feel the same as it always has?",
                "What is the earliest memory I have of responding this way?",
                "What might this response have been protecting me from, back then?",
              ],
            },
            {
              type: "prompts",
              title: "Look forward gently",
              items: [
                "What is one small situation this week where I might notice this pattern in real time?",
                "What would it feel like to simply notice the response, without needing to change it yet?",
              ],
            },
            {
              type: "note",
              text: "If this exercise brings up strong feelings, it's okay to pause and use a grounding technique, or come back to it another day.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Small, practical ways to notice and gently work with your responses.",
      intro:
        "These exercises are optional and can be adapted to whatever pace feels manageable. You are never required to complete them in one sitting.",
      resources: [
        {
          slug: "noticing-the-shift",
          kind: "practice",
          title: "Noticing the shift towards regulation",
          summary: "A short practice for pausing between trigger and reaction.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "This practice isn't about stopping a trauma response — that's rarely possible in the moment, and isn't the goal. It's about building a small pause where you can notice what's happening in your body before automatically acting on it.",
            },
            {
              type: "steps",
              title: "Try this when you notice a response building",
              items: [
                { label: "Name it, gently", note: "Silently or aloud: \"This might be my fight/flight/freeze/fawn response.\"" },
                { label: "Locate it in your body", note: "Where do you feel it most? Chest, jaw, stomach, hands?" },
                { label: "Take one slower breath", note: "Not to fix anything — just to create a small gap." },
                { label: "Ask what you need right now", note: "Space, water, to sit down, to step outside — whatever feels true." },
                { label: "Let yourself respond in your own time", note: "You don't have to react instantly just because the urge is instant." },
              ],
            },
            {
              type: "note",
              text: "You can stop this practice at any point. If it feels like too much in the moment, that's useful information too — it may be a sign to reach for grounding instead.",
            },
          ],
        },
        {
          slug: "grounding-for-activation",
          kind: "practice",
          title: "Grounding when a response feels activated",
          summary: "A simple grounding sequence for fight, flight or freeze states.",
          minutes: 5,
          blocks: [
            {
              type: "steps",
              title: "A short grounding sequence",
              items: [
                { label: "Feel your feet", note: "Press them into the floor. Notice the surface beneath you." },
                { label: "Look around and name five things you can see", note: "Ordinary objects are fine." },
                { label: "Slow your exhale", note: "Try breathing out for longer than you breathe in." },
                { label: "Name where you are and today's date", note: "This can help re-orient you to the present moment." },
                { label: "Notice one thing that is okay right now", note: "Even something small, like a comfortable chair." },
              ],
            },
            {
              type: "prose",
              text: "Our full Grounding tool has more techniques if this one doesn't fit for you — different approaches work for different people and different moments.",
            },
          ],
        },
        {
          slug: "trauma-response-worksheet",
          kind: "worksheet",
          title: "Mapping my trauma response patterns",
          summary: "A printable worksheet to track situations, responses, body signals and what helped.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "This worksheet is designed to be used after a triggering moment has passed, once you have some distance and space to reflect. Fill in as much or as little as feels useful — there's no need to complete every field.",
            },
            {
              type: "note",
              text: "This is a self-reflection tool, not a clinical assessment. If you notice a pattern that concerns you, consider bringing this worksheet to a conversation with a therapist or GP.",
            },
          ],
          worksheetFields: [
            { label: "The situation", hint: "What was happening just before you noticed a response?", lines: 3 },
            { label: "The response", hint: "Fight, flight, freeze, fawn, or a mix — describe what you did or felt the urge to do", lines: 2 },
            { label: "Body signals", hint: "What did you notice physically, and where in your body?", lines: 3 },
            { label: "What helped, if anything", hint: "A breath, stepping away, talking to someone, grounding, time alone", lines: 3 },
            { label: "What I noticed afterwards", hint: "Anything you'd like to remember for next time", lines: 3 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts to explore your responses with more depth and self-compassion.",
      resources: [
        {
          slug: "reflecting-with-compassion",
          kind: "reflect",
          title: "Reflecting on my responses with compassion",
          summary: "Journal prompts for exploring these patterns without judgement.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "If my most familiar trauma response could speak, what would it say it's trying to do for me?",
                "What would I say to a friend who described this same pattern in themselves?",
                "Is there a relationship or environment where this response softens? What feels different there?",
                "What is one small piece of evidence, from today or this week, that I am safer now than I once was?",
                "What would 'moving towards regulation' look like for me, in a realistic, small way — not a total transformation, just one step?",
              ],
            },
            {
              type: "note",
              text: "You can save these reflections in your Journal to revisit over time and notice how your patterns and understanding of them may shift.",
            },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "People-Pleasing", to: "/learn/people-pleasing" },
    { label: "Triggers", to: "/learn/triggers" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Grounding", to: "/grounding" },
    { label: "Journal", to: "/journal" },
  ],
};
